// The region whose invoices the app shows (Settings -> Region; spec §4).
// Keep this file free of runtime imports: node:test loads it directly with type stripping.

/** The regions the Command Center serves. Colombia is the default: every invoice before Argentina was Colombian. */
export const REGIONS = ['CO', 'AR'] as const
export type Region = (typeof REGIONS)[number]
export const DEFAULT_REGION: Region = 'CO'

/** The cookie that remembers the region per browser, like the language. */
export const REGION_COOKIE = 'AP_REGION'
/** The header every API call carries; the backend picks the workspace from it (app/core/region.py). */
export const REGION_HEADER = 'X-AP-Region'
/** Raised on window when the region changes, so anything showing it can re-read it. */
export const REGION_CHANGED_EVENT = 'ap:region-changed'
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

export function isRegion(value: unknown): value is Region {
  return typeof value === 'string' && (REGIONS as readonly string[]).includes(value)
}

/** The region stored in a `document.cookie` string; anything missing or unknown is Colombia. */
export function regionFromCookie(cookie: string): Region {
  for (const part of cookie.split(';')) {
    const [name, ...rest] = part.trim().split('=')
    if (name === REGION_COOKIE) {
      const value = decodeURIComponent(rest.join('='))
      return isRegion(value) ? value : DEFAULT_REGION
    }
  }
  return DEFAULT_REGION
}

/** The `document.cookie` assignment that stores `region`, scoped to the app's base path. */
export function regionCookie(region: Region, basePath = ''): string {
  const path = basePath && basePath !== '/' ? basePath : '/'
  return `${REGION_COOKIE}=${region}; Path=${path}; Max-Age=${ONE_YEAR_SECONDS}; SameSite=Lax`
}

/** The region of this browser right now; Colombia on the server. */
export function currentRegion(): Region {
  return typeof document === 'undefined' ? DEFAULT_REGION : regionFromCookie(document.cookie)
}
